import Textbox from "./activityComponents/Textbox";
import TextArea from "./activityComponents/TextArea";
import Checkboxes from "./activityComponents/Checkboxes";
import MatrixGauge from "./activityComponents/MatrixGauge";
import TwoDGauge from "./activityComponents/TwoDGauge";
import Timer from "./activityComponents/Timer";
import ActivitySlider from "./activityComponents/ActivitySlider";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { convertOldCustomToNew, getDirForLanguage, LANGUAGES } from "../lib/utils";
import { RecordingPlayer } from "./recording/RecordingPlayer";
import { MediaNotePlayer } from "./recording/MediaNotePlayer";
import { ImageViewer } from "./images/ImageViewer";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import "./RenderReviewDialog.css";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { TiptapProvider } from "../context/TipTapProvider";
import TipTap from "../components/richtext/TipTap";
import SmartList from "../components/activityComponents/SmartList";
import SmartTable from "../components/activityComponents/SmartTable";
import MentalModelStudio from "../components/activityComponents/MentalModelStudio";
import ThoughtOrganizer from "../components/activityComponents/ThoughtOrganizer";
import TextMarkup from "../components/activityComponents/TextMarkup";
import CategoriesBoard from "./activityComponents/CategoriesBoard";
import PersonalDiagram from "./activityComponents/PersonalDiagram";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { FieldActionMenu } from "./ui/field-action-menu";
import { FieldContextMenu } from "./ui/field-context-menu";
import DecorationButton from "./DecorationButton";
import PaneNoteStickyCard from "./runActivity/exampleFollower/PaneNoteStickyCard";
import AnnotationEpilogueScreen from "./runActivity/AnnotationEpilogueScreen";
import { useState, useEffect, useContext, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import useRetryFetch from "../hooks/useRetryFetch";
import useAuth from "../hooks/useAuth";
import { config } from "../Environment";
import LoggerContext from "../context/LoggerProvider";
import { formatFieldValue as formatFieldValueShared } from "../lib/formatFieldValue";

// Helpers to extract note text/recordings (same as PaneNotesButton)
const getNoteText = (noteData) => {
  if (!noteData) return "";
  if (typeof noteData === "string") return noteData;
  if (typeof noteData === "object" && noteData.text !== undefined)
    return noteData.text;
  return "";
};
const getNoteRecordings = (noteData) => {
  if (!noteData) return null;
  if (typeof noteData === "object" && noteData.recordings)
    return noteData.recordings;
  return null;
};
const getNoteImage = (noteData, activityId) => {
  if (!noteData || typeof noteData !== "object" || !activityId)
    return undefined;
  return noteData.images?.[activityId];
};

const RenderReviewDialog = forwardRef(({
  logEntry,
  onQuotePane,
  onCopyToAction,
  onCopyToInsight,
  notes = {},
  processId,
  isProcessOwner = false,
  prologue = "",
  epilogue = "",
  isEditMode = false,
  onPrologueChange,
  onEpilogueChange,
  onUserDataChange,
  prologueRecordings,
  epilogueRecordings,
  /** When provided, notes are managed by parent (e.g. example edit mode) - no backend save */
  onNotesChange: onNotesChangeFromParent = undefined,
  /** Name of the example creator — shown in the sticky note card when reading an example */
  exampleCreatorName = undefined,
  /** Parent navigation handlers — intercepted for Mode 3 epilogue overlay */
  onNext,
  onPrevious,
  /** Direct prescription ID — used in edit mode where processId is undefined */
  prescriptionId: prescriptionIdProp,
  /** Full prescription diagram data map for personal_diagram field label resolution */
  diagramData: diagramDataProp = null,
}, ref) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const { Logger } = useContext(LoggerContext);
  const { user: currentUser } = useAuth();
  const [paneNotes, setPaneNotes] = useState(notes);
  const saveTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);

  // Local annotation display mode — overrides logEntry value after author changes it
  const [localAnnotationMode, setLocalAnnotationMode] = useState(null);
  useEffect(() => { setLocalAnnotationMode(null); }, [logEntry?.activity]);
  const annotationDisplayMode = localAnnotationMode ?? (logEntry?.activityDialog?.annotationDisplayMode || "on_demand");

  // Sticky card force-show (triggered by sparkles button in read-only mode)
  const [stickyCardForceVisible, setStickyCardForceVisible] = useState(false);
  useEffect(() => { setStickyCardForceVisible(false); }, [logEntry?.activity]);

  // Mode 3 epilogue-style overlay — shown when user navigates AWAY from a pane (not on arrival)
  const [epilogueOverlayVisible, setEpilogueOverlayVisible] = useState(false);
  const pendingNavRef = useRef(null); // stores the nav fn to call after Continue
  const seenEpilogueRef = useRef(new Set());

  // Reset overlay and pending nav when pane changes
  useEffect(() => {
    setEpilogueOverlayVisible(false);
    pendingNavRef.current = null;
  }, [logEntry?.activity]);

  const makeNavHandler = (navFn) => {
    if (!navFn) return undefined;
    return () => {
      const activityId = logEntry?.activity;
      if (annotationDisplayMode === "epilogue_style" && activityId && !seenEpilogueRef.current.has(activityId)) {
        const noteText = getNoteText(paneNotes[activityId]);
        const hasNote =
          (noteText && noteText.replace(/<[^>]*>/g, "").trim().length > 0) ||
          !!getNoteRecordings(paneNotes[activityId]);
        if (hasNote) {
          seenEpilogueRef.current.add(activityId);
          pendingNavRef.current = navFn;
          setEpilogueOverlayVisible(true);
          return;
        }
      }
      navFn();
    };
  };

  const handleEpilogueContinue = () => {
    setEpilogueOverlayVisible(false);
    const fn = pendingNavRef.current;
    pendingNavRef.current = null;
    fn?.();
  };

  // Expose intercepted nav to parent via ref
  useImperativeHandle(ref, () => ({
    interceptNext: makeNavHandler(onNext),
    interceptPrevious: makeNavHandler(onPrevious),
  }), [onNext, onPrevious, logEntry?.activity, annotationDisplayMode, paneNotes]);

  // Determine if current user is the activity author (needed to show mode selector)
  const [isActivityAuthor, setIsActivityAuthor] = useState(false);
  useEffect(() => {
    if (isProcessOwner) { setIsActivityAuthor(true); return; }
    if (!processId || !currentUser) { setIsActivityAuthor(false); return; }
    (async () => {
      try {
        const res = await retryFetch(`${config.API_BASE}/api/processes/${processId}`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        const prescriptionId = data?.fiPrescription;
        if (!prescriptionId) return;
        const presRes = await retryFetch(`${config.API_BASE}/api/prescriptions/${prescriptionId}`, { credentials: "include" });
        if (!presRes.ok) return;
        const presData = await presRes.json();
        const authorId = typeof presData.fiUser === "string" ? presData.fiUser : presData.fiUser?._id;
        const userId = currentUser._id || currentUser.id;
        setIsActivityAuthor(String(userId) === String(authorId));
      } catch { setIsActivityAuthor(false); }
    })();
  }, [processId, currentUser, isProcessOwner]);

  // Save annotation display mode — uses dedicated endpoint that also patches PrescriptionHistory
  const handleAnnotationDisplayModeChange = useCallback(async (mode) => {
    if (!logEntry?.activity) return;
    setLocalAnnotationMode(mode);
    try {
      let prescriptionId = prescriptionIdProp;
      if (!prescriptionId) {
        if (!processId) return;
        const procRes = await retryFetch(`${config.API_BASE}/api/processes/${processId}`, { credentials: "include" });
        if (!procRes.ok) return;
        const procData = await procRes.json();
        prescriptionId = procData?.fiPrescription;
      }
      if (!prescriptionId) return;
      await retryFetch(`${config.API_BASE}/api/prescriptions/${prescriptionId}/annotation-mode`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ activityId: logEntry.activity, mode }),
      });
      Logger.debug("[RenderReviewDialog] Annotation display mode saved:", mode);
    } catch (err) {
      Logger.error("[RenderReviewDialog] Failed to save annotation display mode:", err);
    }
  }, [processId, prescriptionIdProp, logEntry?.activity, retryFetch, Logger]);

  // Update notes when props change (only if not currently saving and values differ)
  useEffect(() => {
    if (
      !isSavingRef.current &&
      JSON.stringify(notes) !== JSON.stringify(paneNotes)
    ) {
      setPaneNotes(notes);
    }
  }, [notes, paneNotes]);

  // Save notes to backend (debounced)
  const saveNotesToBackend = useCallback(
    async (notesToSave) => {
      if (!processId || isSavingRef.current) return;

      try {
        isSavingRef.current = true;
        const response = await retryFetch(
          `${config.API_BASE}/api/processes/${processId}/notes`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ notes: notesToSave }),
          },
        );

        if (response.ok) {
          Logger.debug("[RenderReviewDialog] Notes saved successfully");
        } else {
          const error = await response.json();
          Logger.error("[RenderReviewDialog] Failed to save notes:", error);
        }
      } catch (error) {
        Logger.error("[RenderReviewDialog] Failed to save notes:", error);
      } finally {
        isSavingRef.current = false;
      }
    },
    [processId, retryFetch, Logger],
  );

  // Handler for updating notes for current activity (with debouncing)
  const handleNotesChange = useCallback(
    (value, immediate = false) => {
      if (!logEntry?.activity) return;

      const existing = paneNotes[logEntry.activity];
      const existingObj =
        typeof existing === "object" && existing !== null ? existing : {};
      const updatedNotes = {
        ...paneNotes,
        [logEntry.activity]: { ...existingObj, text: value },
      };

      setPaneNotes(updatedNotes);

      if (onNotesChangeFromParent) {
        onNotesChangeFromParent(updatedNotes);
        return;
      }

      // Save to backend (when parent doesn't manage notes)
      if (processId) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }
        if (immediate) {
          saveNotesToBackend(updatedNotes);
        } else {
          saveTimeoutRef.current = setTimeout(() => {
            saveNotesToBackend(updatedNotes);
          }, 1000);
        }
      }
    },
    [
      logEntry?.activity,
      paneNotes,
      processId,
      saveNotesToBackend,
      onNotesChangeFromParent,
    ],
  );

  // Merge pane recordings/image into notes (image is saved by process image API; we only update local state)
  const handlePaneRecordingsChange = useCallback(
    (activityId, newRecordings) => {
      const existing = paneNotes[activityId];
      const existingObj =
        typeof existing === "object" && existing !== null
          ? existing
          : { text: typeof existing === "string" ? existing : "" };
      const updatedNotes = {
        ...paneNotes,
        [activityId]: {
          ...existingObj,
          recordings: {
            audio: newRecordings?.audio,
            video: newRecordings?.video,
          },
          images:
            newRecordings?.image != null
              ? { [activityId]: newRecordings.image }
              : existingObj.images || {},
        },
      };
      setPaneNotes(updatedNotes);
      if (onNotesChangeFromParent) {
        onNotesChangeFromParent(updatedNotes);
      }
    },
    [paneNotes, onNotesChangeFromParent],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to quote a specific field in chat
  const handleQuoteField = (fieldLabel, fieldKey) => {
    if (onQuotePane && logEntry?.activity) {
      const quoteData = {
        screen: logEntry.activity,
        screenLabel:
          logEntry?.activityDialog?.label?.[logEntry?.prescriptionLanguage] ||
          logEntry.activity,
        component: fieldLabel,
        componentKey: fieldKey,
      };

      console.log("[RenderReviewDialog] Quoting field:", quoteData);
      onQuotePane(quoteData);
    } else {
      console.warn(
        "[RenderReviewDialog] Cannot quote field - onQuotePane or activity missing",
        {
          hasOnQuotePane: !!onQuotePane,
          activity: logEntry?.activity,
        },
      );
    }
  };

  // Helper function to copy field content as an Action Item
  const handleCopyToAction = (content, label) => {
    if (onCopyToAction && content.trim()) {
      console.log(
        "[RenderReviewDialog] Copying to Action Item:",
        label,
        content,
      );
      onCopyToAction(content, label);
    } else {
      console.warn(
        "[RenderReviewDialog] Cannot copy to action - onCopyToAction missing or content empty",
      );
    }
  };

  // Helper function to copy field content as an Insight
  const handleCopyToInsight = (content, label) => {
    if (onCopyToInsight && content.trim()) {
      console.log("[RenderReviewDialog] Copying to Insight:", label, content);
      onCopyToInsight(content, label);
    } else {
      console.warn(
        "[RenderReviewDialog] Cannot copy to insight - onCopyToInsight missing or content empty",
      );
    }
  };

  const formatFieldValue = (fieldValue, fieldOptions) =>
    formatFieldValueShared(fieldValue, fieldOptions);

  // Helper to get selected checkbox labels as an array (for creating individual action items/insights)
  const getSelectedCheckboxLabels = (fieldValue, fieldOptions) => {
    if (typeof fieldValue !== "object" || fieldValue === null || !fieldOptions)
      return [];
    return fieldOptions
      .filter((opt) => fieldValue[opt.key] === true)
      .map((opt) => opt.label);
  };

  // Helper to get text alignment based on language direction
  const getTextAlign = () => {
    return LANGUAGES[logEntry?.prescriptionLanguage]?.dir === "rtl"
      ? "right"
      : "left";
  };

  const renderItem = (item) => {
    switch (item.type) {
      // informative elements
      case "interpolation":
        return (
          <div
            className="mt-4"
            style={{ textAlign: getTextAlign() }}
            dangerouslySetInnerHTML={{ __html: interpolate(item.content) }}
          ></div>
        );
      case "aiprompt": {
        const aiResponse = logEntry?.userData[item.id];
        if (!aiResponse) return null;
        const responseFormat = item.settings?.responseFormat || "plain";
        if (responseFormat === "markdown") {
          return (
            <div className="mt-4 text-start prose prose-sm max-w-none">
              <ReactMarkdown>{aiResponse}</ReactMarkdown>
            </div>
          );
        }
        if (responseFormat === "richtext") {
          return (
            <div
              className="mt-4 text-start prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: aiResponse }}
            />
          );
        }
        // Default: plain text
        return (
          <div className="mt-4 text-start whitespace-pre-wrap">
            {aiResponse}
          </div>
        );
      }
      case "personal_diagram": {
        // Prefer full diagram map from parent; fall back to current activity only
        // (same-pane checkbox labels still resolve when parent omitted diagramData).
        const resolvedDiagramData =
          diagramDataProp ||
          (logEntry?.activity && logEntry?.activityDialog
            ? { [logEntry.activity]: logEntry.activityDialog }
            : null);
        return (
          <PersonalDiagram
            content={item.content}
            userData={logEntry?.userData || {}}
            diagramData={resolvedDiagramData}
          />
        );
      }
      case "richtext":
      case "richtextnew":
        return (
          <div
            className="ProseMirror mt-4"
            dir={getDirForLanguage(logEntry?.prescriptionLanguage || i18n.language)}
            dangerouslySetInnerHTML={{ __html: item.content }}
          ></div>
        );
      case "richtext_dynamic":
        return (
          <div
            className="ProseMirror mt-4"
            dir={getDirForLanguage(logEntry?.prescriptionLanguage || i18n.language)}
            dangerouslySetInnerHTML={{ __html: logEntry?.userData[item.id] }}
          ></div>
        );
      case "hr":
        return <hr />;
      case "headline1":
        return <h2 className="text-2xl text-bold">{item.content}</h2>;
      case "headline2":
        return (
          <h3 className="text-lg my-1.5 leading-tight text-start">
            {item.content}
          </h3>
        );
      case "paragraph":
        return <p>{item.content}</p>;
      case "orderedlist":
        return (
          <div className="optioncomponent text-start">
            <Label className="font-semibold">{item.content.label}</Label>
            <ol style={{ listStyleType: "decimal", marginLeft: "16px" }}>
              {item.content.items.map((olitem) => (
                <li key={olitem.key}>{olitem.label}</li>
              ))}
            </ol>
          </div>
        );
      // interactive elements
      case "textbox":
        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={logEntry?.userData[item.content.key] || ""}
            onQuote={
              onQuotePane
                ? () =>
                    handleQuoteField(
                      item.content.label || item.content.key,
                      item.content.key,
                    )
                : undefined
            }
            onCopyToAction={
              onCopyToAction
                ? (content) =>
                    handleCopyToAction(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            onCopyToInsight={
              onCopyToInsight
                ? (content) =>
                    handleCopyToInsight(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
          >
            <div data-field-id={item.content.key}>
              {isEditMode ? (
                // Editable mode - use controlled input
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {item.content.label}
                  </label>
                  <input
                    type="text"
                    value={logEntry?.userData[item.content.key] || ""}
                    onChange={(e) =>
                      onUserDataChange &&
                      onUserDataChange(item.content.key, e.target.value)
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder={item.content.placeholder}
                  />
                </div>
              ) : (
                // Read-only mode - use original component
                <Textbox
                  itemkey={item.id}
                  content={item.content}
                  defaultValue={logEntry?.userData[item.content.key]}
                />
              )}
            </div>
          </FieldActionMenu>
        );
      case "textarea":
        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={logEntry?.userData[item.content.key] || ""}
            onQuote={
              onQuotePane
                ? () =>
                    handleQuoteField(
                      item.content.label || item.content.key,
                      item.content.key,
                    )
                : undefined
            }
            onCopyToAction={
              onCopyToAction
                ? (content) =>
                    handleCopyToAction(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
            onCopyToInsight={
              onCopyToInsight
                ? (content) =>
                    handleCopyToInsight(
                      content,
                      item.content.label || item.content.key,
                    )
                : undefined
            }
          >
            <div data-field-id={item.content.key}>
              {isEditMode ? (
                // Editable mode - use controlled textarea
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {item.content.label}
                  </label>
                  <textarea
                    value={logEntry?.userData[item.content.key] || ""}
                    onChange={(e) =>
                      onUserDataChange &&
                      onUserDataChange(item.content.key, e.target.value)
                    }
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
                    placeholder={item.content.placeholder}
                    rows={4}
                  />
                </div>
              ) : (
                // Read-only mode - use original component
                <TextArea
                  itemkey={item.id}
                  content={item.content}
                  defaultValue={logEntry?.userData[item.content.key]}
                />
              )}
            </div>
          </FieldActionMenu>
        );
      case "checkbox":
        return (
          <FieldActionMenu
            fieldLabel={item.content.label || item.content.key}
            fieldValue={formatFieldValue(
              logEntry?.userData[item.content.key] || "",
              item.content.options,
            )}
            onQuote={
              onQuotePane
                ? () =>
                    handleQuoteField(
                      item.content.label || item.content.key,
                      item.content.key,
                    )
                : undefined
            }
            onCopyToAction={
              onCopyToAction
                ? () => {
                    const labels = getSelectedCheckboxLabels(
                      logEntry?.userData[item.content.key],
                      item.content.options,
                    );
                    labels.forEach((labelText) =>
                      handleCopyToAction(
                        labelText,
                        item.content.label || item.content.key,
                      ),
                    );
                  }
                : undefined
            }
            onCopyToInsight={
              onCopyToInsight
                ? () => {
                    const labels = getSelectedCheckboxLabels(
                      logEntry?.userData[item.content.key],
                      item.content.options,
                    );
                    labels.forEach((labelText) =>
                      handleCopyToInsight(
                        labelText,
                        item.content.label || item.content.key,
                      ),
                    );
                  }
                : undefined
            }
            disabled={!onQuotePane && !onCopyToAction && !onCopyToInsight}
          >
            <div data-field-id={item.content.key}>
              {isEditMode && onUserDataChange ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium block">
                    {item.content.label}
                  </label>
                  <div className="space-y-1.5">
                    {(item.content.options || []).map((option) => {
                      const currentVal =
                        logEntry?.userData[item.content.key] ?? {};
                      const isChecked = currentVal[option.key] === true;
                      const minS = item.content.minSelections ?? 0;
                      const rawMax = item.content.maxSelections;
                      const maxS =
                        rawMax == null || rawMax === 0
                          ? (item.content.options?.length ?? 0)
                          : Math.min(rawMax, item.content.options?.length ?? 0);
                      const selectedCount = Object.values(currentVal).filter(
                        (v) => v === true,
                      ).length;
                      const isDisabled = !isChecked && selectedCount >= maxS;
                      return (
                        <div
                          key={option.key}
                          className={`flex items-center p-2 rounded-lg border transition-all ${
                            isChecked
                              ? "bg-primary/5 border-primary/40"
                              : "bg-white border-gray-200"
                          } ${isDisabled ? "opacity-50" : "cursor-pointer"}`}
                        >
                          <Checkbox
                            id={option.key}
                            checked={isChecked}
                            disabled={isDisabled}
                            onCheckedChange={(checked) => {
                              if (checked === true && selectedCount >= maxS)
                                return;
                              const next = {
                                ...currentVal,
                                [option.key]: checked === true,
                              };
                              onUserDataChange(item.content.key, next);
                            }}
                          />
                          <Label
                            htmlFor={option.key}
                            className="text-sm ml-2 flex-1 cursor-pointer"
                          >
                            {option.label}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <Checkboxes
                  itemkey={item.id}
                  content={item.content}
                  defaultValue={logEntry?.userData[item.content.key]}
                  value={logEntry?.userData[item.content.key]}
                  readOnly={!isEditMode}
                  validationAttempt={0}
                />
              )}
            </div>
          </FieldActionMenu>
        );
      case "gauge":
        return (
          <FieldContextMenu
            fieldLabel={item.content.label || item.content.key}
            fieldKey={item.content.key}
            onQuote={() =>
              handleQuoteField(
                item.content.label || item.content.key,
                item.content.key,
              )
            }
            disabled={!onQuotePane}
          >
            <div
              data-field-id={item.content.key}
              className="min-w-0 overflow-hidden"
            >
              <MatrixGauge
                itemkey={item.id}
                content={item.content}
                defaultValue={logEntry?.userData[item.content.key]}
                value={
                  isEditMode && onUserDataChange
                    ? (logEntry?.userData[item.content.key] ?? {
                        x: 50,
                        y: 50,
                        quadrant: "Q1",
                        quadrantLabel: "",
                      })
                    : undefined
                }
                onChange={
                  isEditMode && onUserDataChange
                    ? (val) => onUserDataChange(item.content.key, val)
                    : undefined
                }
                compact
                readOnly={!isEditMode}
                connectedListKey={(item.content.linkedListKeys || [])[0]}
                connectedListItems={(() => {
                  const key = (item.content.linkedListKeys || [])[0];
                  if (!key) return undefined;
                  const raw = logEntry?.userData?.[key];
                  return Array.isArray(raw?.items) ? raw.items : undefined;
                })()}
              />
            </div>
          </FieldContextMenu>
        );
      case "twodgauge":
        return (
          <FieldContextMenu
            fieldLabel={item.content.label || item.content.key}
            fieldKey={item.content.key}
            onQuote={() =>
              handleQuoteField(
                item.content.label || item.content.key,
                item.content.key,
              )
            }
            disabled={!onQuotePane}
          >
            <div
              data-field-id={item.content.key}
              className="min-w-0 overflow-hidden"
            >
              <TwoDGauge
                itemkey={item.id}
                content={item.content}
                defaultValue={logEntry?.userData[item.content.key]}
                value={
                  isEditMode && onUserDataChange
                    ? (logEntry?.userData[item.content.key] ?? {})
                    : undefined
                }
                onChange={
                  isEditMode && onUserDataChange
                    ? (val) => onUserDataChange(item.content.key, val)
                    : undefined
                }
                label={item.content.label}
                xAxisLabel={item.content.xAxisLabel || "Internal"}
                yAxisLabel={item.content.yAxisLabel || "External"}
                enableFeedback={item.content.enableFeedback !== false}
                lowProgressZone={
                  item.content.lowProgressZone || {
                    x: 20,
                    y: 20,
                    operator: "AND",
                  }
                }
                highProgressZone={
                  item.content.highProgressZone || {
                    x: 75,
                    y: 75,
                    operator: "OR",
                  }
                }
                lowProgressMessage={item.content.lowProgressMessage}
                highProgressMessage={item.content.highProgressMessage}
                placeholderMessage={item.content.placeholderMessage}
                compact
                readOnly={!isEditMode}
              />
            </div>
          </FieldContextMenu>
        );
      case "timer":
        return (
          <Timer
            itemkey={item.id}
            content={item.content}
            defaultValue={item.content?.countdownValue || "00:00"}
            value={logEntry?.userData?.[item.content.key]}
            mode={item.content?.mode}
            countdownValue={item.content?.countdownValue}
            alarmSound={
              item.content?.alarmSound === true
                ? "bell"
                : item.content?.alarmSound === false ||
                    item.content?.alarmSound == null
                  ? null
                  : item.content?.alarmSound
            }
            allowDynamicTime={item.content?.allowDynamicTime}
            dynamicTimeLabel={item.content?.dynamicTimeLabel}
            onChange={
              isEditMode && onUserDataChange
                ? (key, val) => onUserDataChange(key, val)
                : undefined
            }
          />
        );
      case "slider":
        return (
          <FieldContextMenu
            fieldLabel={item.content.label || item.content.key}
            fieldKey={item.content.key}
            onQuote={() =>
              handleQuoteField(
                item.content.label || item.content.key,
                item.content.key,
              )
            }
            disabled={!onQuotePane}
          >
            <div data-field-id={item.content.key}>
              <ActivitySlider
                itemkey={item.id}
                content={item.content}
                defaultValue={logEntry?.userData[item.content.key]}
                readOnly={!isEditMode}
                onChange={
                  isEditMode && onUserDataChange
                    ? (key, val) => onUserDataChange(key, val)
                    : undefined
                }
              />
            </div>
          </FieldContextMenu>
        );
      case "smart_list": {
        const smartListData = logEntry?.userData?.[item.content.key];
        const savedItems = Array.isArray(smartListData)
          ? smartListData
          : smartListData?.items || [];
        return (
          <SmartList
            readonly={true}
            content={item.content}
            config={{
              ...item.content.config,
              initialItems: savedItems,
            }}
          />
        );
      }
      case "smart_table": {
        const savedTable = logEntry?.userData?.[item.content.key];
        return (
          <SmartTable
            readonly={true}
            content={item.content}
            config={{
              ...item.content.config,
              initialRows: savedTable?.rows || [],
            }}
            userData={{ [item.content.key]: savedTable }}
            lang={logEntry?.prescriptionLanguage}
          />
        );
      }
      case "thoughts_organizer":
        return (
          <ThoughtOrganizer
            readonly={true}
            content={item.content}
            config={{ ...item.content.config }}
            userData={logEntry?.userData?.[item.content.key]}
            initialText={logEntry?.userData?.[item.content.key]?.text}
          ></ThoughtOrganizer>
        );
      case "text_markup":
        return (
          <TextMarkup
            content={item.content}
            userData={logEntry?.userData?.[item.content.key]}
            initialValue={logEntry?.userData?.[item.content.key]}
            readonly={true}
            dir={getDirForLanguage(logEntry?.prescriptionLanguage || i18n.language)}
          />
        );
      case "categories_board":
        return (
          <CategoriesBoard
            readonly={true}
            content={item.content}
            config={{ ...item.content.config }}
            userData={logEntry?.userData}
          ></CategoriesBoard>
        );
      case "mental_model_studio":
      case "mental_model_path":
        return (
          <MentalModelStudio
            readonly={true}
            content={item.content}
            config={{ ...item.content.config }}
            userData={logEntry?.userData || {}}
            onChange={() => {}}
          />
        );
      case "audio_note":
      case "video_note":
        const reviewMediaType = item.type === "audio_note" ? "audio" : "video";
        const reviewMediaItemId = `${logEntry?.activity}-${item.id}`;

        // Review mode never autoplays media notes — the reviewer has
        // unlimited time and can choose to play them; forcing playback
        // (e.g. hearing your own voice every time you revisit) is unwanted
        // here even when the author enabled autoplay for engagement.
        return item.content?.recording ? (
          <div className="my-3">
            <MediaNotePlayer
              recording={item.content.recording}
              type={reviewMediaType}
              label={item.content.label}
              autoPlay={false}
              itemId={reviewMediaItemId}
            />
          </div>
        ) : null;
      case "audio_input":
      case "video_input":
        const reviewRecording = logEntry?.userData[item.content.key];
        const reviewType = item.type === "audio_input" ? "audio" : "video";

        return (
          <div className="p-3 my-3 border rounded-lg bg-muted/10">
            {item.content.label && (
              <p className="mb-2 text-sm font-medium text-muted-foreground">
                {item.content.label}
              </p>
            )}
            {reviewRecording ? (
              <RecordingPlayer
                recording={reviewRecording}
                type={reviewType}
                compact={true}
                readOnly={true}
              />
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {t("no_recording_provided") || "No recording was provided"}
              </p>
            )}
          </div>
        );
      case "image_note":
        return item.content?.image ? (
          <div className="p-3 my-3 border rounded-lg bg-muted/10">
            {item.content.label && (
              <p className="mb-2 text-sm font-medium text-muted-foreground">
                {item.content.label}
              </p>
            )}
            <ImageViewer
              image={item.content.image}
              compact={true}
              readOnly={true}
            />
          </div>
        ) : null;
      case "image_input":
        const reviewImage = logEntry?.userData[item.content.key];

        return (
          <div className="p-3 my-3 border rounded-lg bg-muted/10">
            {item.content.label && (
              <p className="mb-2 text-sm font-medium text-muted-foreground">
                {item.content.label}
              </p>
            )}
            {reviewImage ? (
              <ImageViewer image={reviewImage} compact={true} readOnly={true} />
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {t("no_image_provided") || "No image was provided"}
              </p>
            )}
          </div>
        );
      default:
    }
  };

  const interpolate = (content) => {
    let result = content;

    let userData = logEntry?.userData;

    if (userData) {
      let matches = result.match(/\{.*?\}/g);

      if (!matches) return content;

      for (let match of matches) {
        let variableName = match.match(/\{(.*)\}/)[1];
        let value = userData[variableName];
        if (value === undefined || value === null || value === "") {
          let newPart = `<b class="text-orange-500 italic">[${variableName}]</b>`;
          result = result.replace(match, newPart);
        } else {
          let transformed = structuredClone(value);
          if (typeof transformed !== "string") {
            transformed = JSON.stringify(transformed);
          }
          let newPart = "<b>" + transformed + "</b>";
          result = result.replace(match, newPart);
        }
      }
    }
    return result;
  };

  const renderActivityComponents = () => {
    let components = logEntry?.activityDialog.custom;

    if (components && !Array.isArray(components)) {
      components = convertOldCustomToNew(components);
    }
    if (components) {
      return (
        <>
          {components.map((item) => (
            <div className="mx-5" key={item.id}>
              {renderItem(item)}
            </div>
          ))}
        </>
      );
    } else {
      return <></>;
    }
  };

  return (
    <div className="relative">
      <Card className="relative max-w-4xl mx-auto h-full md:h-auto flex flex-col md:rounded-md md:overflow-hidden md:hidden border-none md:border">
        {/* Screen Header - Fixed */}
        <CardHeader className="bg-primary text-white border-b py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={!onPrevious}
                onClick={makeNavHandler(onPrevious)}
                className={`flex items-center space-x-1 h-7 px-2 ${
                  (LANGUAGES[logEntry?.prescriptionLanguage]?.dir === "rtl"
                    ? "rtl:rotate-180"
                    : "") +
                  (logEntry?.selectedOptionId === "GOINGBACK"
                    ? " highlightedOption"
                    : "")
                }`}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!onNext}
                onClick={makeNavHandler(onNext)}
                className={`flex items-center space-x-1 h-7 px-2 ${
                  (LANGUAGES[logEntry?.prescriptionLanguage]?.dir === "rtl"
                    ? "rtl:rotate-180"
                    : "") +
                  (logEntry?.selectedOptionId === "GOINGFORWARD"
                    ? " highlightedOption"
                    : "")
                }`}
              >
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
            <CardTitle className="text-lg md:text-xl font-semibold text-center flex-1 truncate px-2">
              {logEntry?.activityDialog.label?.[logEntry?.prescriptionLanguage]}
            </CardTitle>
            <div className="flex items-center gap-1">
              {/* Annotations (Pane Notes, Prologue, Epilogue) - same as engagement */}
              <DecorationButton
                paneNotes={getNoteText(paneNotes[logEntry?.activity])}
                onPaneNotesChange={(value) => handleNotesChange(value, true)}
                paneRecordings={(() => {
                  const noteData = paneNotes[logEntry?.activity];
                  const base = getNoteRecordings(noteData);
                  const img = getNoteImage(noteData, logEntry?.activity);
                  return base
                    ? { ...base, image: base.image ?? img }
                    : img
                      ? { image: img }
                      : {};
                })()}
                onPaneRecordingsChange={
                  isProcessOwner
                    ? (recordings) =>
                        handlePaneRecordingsChange(
                          logEntry?.activity,
                          recordings,
                        )
                    : undefined
                }
                prologue={prologue}
                onPrologueChange={isProcessOwner ? onPrologueChange : undefined}
                prologueRecordings={prologueRecordings || {}}
                epilogue={epilogue}
                onEpilogueChange={isProcessOwner ? onEpilogueChange : undefined}
                epilogueRecordings={epilogueRecordings || {}}
                processId={processId}
                currentActivityId={logEntry?.activity}
                currentPaneName={
                  logEntry?.activityDialog?.label?.[
                    logEntry?.prescriptionLanguage
                  ] || "This Pane"
                }
                isReadOnly={!isProcessOwner}
                showPublishOption={false}
                isActivityAuthor={isActivityAuthor}
                annotationDisplayMode={annotationDisplayMode}
                onAnnotationDisplayModeChange={isActivityAuthor ? handleAnnotationDisplayModeChange : undefined}
                    onShowStickyCard={!isProcessOwner ? () => { setStickyCardForceVisible(false); setTimeout(() => setStickyCardForceVisible(true), 0); } : undefined}
              />
              {/* Quote Pane Button */}
              {onQuotePane && logEntry?.activity && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-white hover:bg-white/20"
                  onClick={() =>
                    onQuotePane({
                      screen: logEntry.activity,
                      screenLabel:
                        logEntry?.activityDialog?.label?.[
                          logEntry?.prescriptionLanguage
                        ] || logEntry.activity,
                    })
                  }
                  title="Quote this pane in chat"
                >
                  <MessageCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <div className="flex-1 overflow-hidden md:overflow-visible">
          <div className="h-full overflow-y-auto md:overflow-visible">
            <CardContent className="space-y-4 py-2 px-0 md:p-6">
              {renderActivityComponents()}
            </CardContent>
          </div>
        </div>

        {/* Footer - Inside Card on Desktop */}
        <div className="border-t bg-white flex-shrink-0 md:block hidden">
          <div className="px-6 py-4">
            <div className="flex justify-center">
              {logEntry?.activityDialog.options?.map((option) => (
                <Button
                  disabled
                  key={option.value}
                  className={`px-4 h-8 min-w-[100px] max-w-[250px] text-xs optionbuttons mb-1 disabled:opacity-80 ${
                    logEntry?.selectedOptionId === option.value
                      ? " highlightedOption"
                      : ""
                  }`}
                >
                  <span className="truncate">
                    {option.text[logEntry?.prescriptionLanguage]}
                  </span>
                </Button>
              ))}
            </div>
          </div>
        </div>
        {epilogueOverlayVisible && !isEditMode && !isProcessOwner && (
          <AnnotationEpilogueScreen
            noteText={getNoteText(paneNotes[logEntry?.activity])}
            recordings={getNoteRecordings(paneNotes[logEntry?.activity])}
            onContinue={handleEpilogueContinue}
          />
        )}
      </Card>

      {/* Desktop View */}
      {/* <pre>{JSON.stringify(logEntry, null, 2)}</pre> */}
      <div className="h-[70vh] flex-col hidden md:flex" dir={i18n.dir()}>
        {/* MAIN AREA – fills the rest; nothing else matters */}
        <main className="flex-1 min-h-0 flex flex-col items-center justify-between md:justify-center p-4">
          {/* CARD – also a vertical flexbox */}
          <Card className="relative max-w-4xl w-[30vw] mx-auto flex flex-col flex-1 min-h-0 md:rounded-md md:overflow-hidden">
            {/* CARD HEADER – fixed */}
            <CardHeader className="bg-primary text-white border-b py-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!onPrevious}
                    onClick={makeNavHandler(onPrevious)}
                    className={`flex items-center space-x-1 h-7 px-2 ${
                      (LANGUAGES[logEntry?.prescriptionLanguage]?.dir === "rtl"
                        ? "rtl:rotate-180"
                        : "") +
                      (logEntry?.selectedOptionId === "GOINGBACK"
                        ? " highlightedOption"
                        : "")
                    }`}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!onNext}
                    onClick={makeNavHandler(onNext)}
                    className={`flex items-center space-x-1 h-7 px-2 ${
                      (LANGUAGES[logEntry?.prescriptionLanguage]?.dir === "rtl"
                        ? "rtl:rotate-180"
                        : "") +
                      (logEntry?.selectedOptionId === "GOINGFORWARD"
                        ? " highlightedOption"
                        : "")
                    }`}
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <CardTitle className="text-lg md:text-xl font-semibold text-center flex-1 truncate px-2">
                  {
                    logEntry?.activityDialog.label?.[
                      logEntry?.prescriptionLanguage
                    ]
                  }
                </CardTitle>
                <div className="flex items-center gap-1">
                  {/* Annotations (Pane Notes, Prologue, Epilogue) - same as engagement */}
                  <DecorationButton
                    paneNotes={getNoteText(paneNotes[logEntry?.activity])}
                    onPaneNotesChange={(value) =>
                      handleNotesChange(value, true)
                    }
                    paneRecordings={(() => {
                      const noteData = paneNotes[logEntry?.activity];
                      const base = getNoteRecordings(noteData);
                      const img = getNoteImage(noteData, logEntry?.activity);
                      return base
                        ? { ...base, image: base.image ?? img }
                        : img
                          ? { image: img }
                          : {};
                    })()}
                    onPaneRecordingsChange={
                      isProcessOwner
                        ? (recordings) =>
                            handlePaneRecordingsChange(
                              logEntry?.activity,
                              recordings,
                            )
                        : undefined
                    }
                    prologue={prologue}
                    onPrologueChange={
                      isProcessOwner ? onPrologueChange : undefined
                    }
                    prologueRecordings={prologueRecordings || {}}
                    epilogue={epilogue}
                    onEpilogueChange={
                      isProcessOwner ? onEpilogueChange : undefined
                    }
                    epilogueRecordings={epilogueRecordings || {}}
                    processId={processId}
                    currentActivityId={logEntry?.activity}
                    currentPaneName={
                      logEntry?.activityDialog?.label?.[
                        logEntry?.prescriptionLanguage
                      ] || "This Pane"
                    }
                    isReadOnly={!isProcessOwner}
                    showPublishOption={false}
                    isActivityAuthor={isActivityAuthor}
                    annotationDisplayMode={annotationDisplayMode}
                    onAnnotationDisplayModeChange={isActivityAuthor ? handleAnnotationDisplayModeChange : undefined}
                    onShowStickyCard={!isProcessOwner ? () => { setStickyCardForceVisible(false); setTimeout(() => setStickyCardForceVisible(true), 0); } : undefined}
                  />
                  {/* Quote Pane Button */}
                  {onQuotePane && logEntry?.activity && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-white hover:bg-white/20"
                      onClick={() =>
                        onQuotePane({
                          screen: logEntry.activity,
                          screenLabel:
                            logEntry?.activityDialog?.label?.[
                              logEntry?.prescriptionLanguage
                            ] || logEntry.activity,
                        })
                      }
                      title="Quote this pane in chat"
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>

            {/* CARD BODY – the only piece that scrolls */}
            <CardContent className="flex-1 min-h-0 overflow-y-auto space-y-4 py-4">
              {renderActivityComponents()}
            </CardContent>

            {/* CARD FOOTER – fixed */}
            <div className="border-t bg-white flex-shrink-0 hidden md:block">
              <div className="px-6 py-3">
                <div className="flex justify-center flex-wrap gap-2">
                  {logEntry?.activityDialog.options?.map((option) => (
                    <Button
                      disabled
                      key={option.value}
                      className={`px-4 h-8 min-w-[100px] max-w-[250px] text-xs optionbuttons disabled:opacity-80 ${
                        logEntry?.selectedOptionId === option.value
                          ? " highlightedOption"
                          : ""
                      }`}
                    >
                      <span className="truncate">
                        {option.text[logEntry?.prescriptionLanguage]}
                      </span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            {epilogueOverlayVisible && !isEditMode && !isProcessOwner && (
              <AnnotationEpilogueScreen
                noteText={getNoteText(paneNotes[logEntry?.activity])}
                recordings={getNoteRecordings(paneNotes[logEntry?.activity])}
                onContinue={handleEpilogueContinue}
              />
            )}
          </Card>
        </main>
      </div>

      {/* Floating sticky-note card — auto_overlay shows automatically; on_demand shows via sparkles click */}
      {!isProcessOwner &&
        (annotationDisplayMode) !== "epilogue_style" && (
        <PaneNoteStickyCard
          activityId={logEntry?.activity}
          noteText={getNoteText(paneNotes[logEntry?.activity])}
          recordings={getNoteRecordings(paneNotes[logEntry?.activity])}
          creatorName={exampleCreatorName}
          autoShow={(annotationDisplayMode) === "auto_overlay"}
          forceVisible={stickyCardForceVisible}
        />
      )}

    </div>
  );
});

export default RenderReviewDialog;
